import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import GallerySkeleton from "@/components/GallerySkeleton";
import { supabase } from "@/integrations/supabase/client";
import { staticMedia, buildAltText, type MediaItem } from "@/data/galleryMedia";

const GallerySection = () => {
  const [selected, setSelected] = useState<number | null>(null);
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [media, setMedia] = useState<MediaItem[]>(staticMedia);
  const [galleryLoading, setGalleryLoading] = useState(true);

  // Fetch CMS media and prepend to static
  useEffect(() => {
    const fetchCmsMedia = async () => {
      const { data } = await supabase
        .from("gallery_media")
        .select("*")
        .order("position", { ascending: true });
      if (data && data.length > 0) {
        const cmsItems: MediaItem[] = data.map((item, idx) => ({
          type: item.type as "image" | "video",
          src: item.url,
          alt: buildAltText(item, idx),
          poster: item.poster_url || undefined,
          mirror: item.mirror,
        }));
        setMedia([...cmsItems, ...staticMedia]);
      }
      setGalleryLoading(false);
    };
    fetchCmsMedia();
  }, []);

  useEffect(() => {
    if (selected !== null && media[selected].type === "video" && videoRef.current) {
      videoRef.current.play();
    }
  }, [selected, media]);

  // Lazy load and autoplay visible videos
  useEffect(() => {
    if (!gridRef.current) return;
    const videos = gridRef.current.querySelectorAll("video[data-src]");
    if (!videos.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            // Lazy load video source
            if (!video.src && video.dataset.src) {
              video.src = video.dataset.src;
              video.load();
            }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { rootMargin: "200px", threshold: 0.1 }
    );

    videos.forEach((v) => observer.observe(v));
    return () => observer.disconnect();
  }, [media]);

  const selectedItem = selected !== null ? media[selected] : null;

  return (
    <section id="galeria" className="py-12 md:py-20 lg:py-28">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 md:mb-14">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">{t("gallery.title")}</h2>
          <p className="text-muted-foreground text-sm md:text-lg max-w-2xl mx-auto">{t("gallery.subtitle")}</p>
        </div>
        {galleryLoading ? <GallerySkeleton /> : <div ref={gridRef} className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
          {media.map((item, i) => (
            <button key={i} onClick={() => setSelected(i)} className="relative overflow-hidden rounded-xl aspect-square group cursor-pointer">
              {item.type === "image" ? (
                <img src={item.src} alt={item.alt} width={600} height={600} decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
              ) : (
                <video
                  data-src={item.src}
                  poster={item.poster}
                  muted
                  playsInline
                  loop
                  preload="none"
                  className={`w-full h-full object-cover ${item.mirror ? '-scale-x-100' : ''}`}
                />
              )}
              <div className="absolute inset-0 bg-navy-dark/0 group-hover:bg-navy-dark/40 transition-colors flex items-end p-4">
                <span className="text-primary-foreground text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0">{item.alt}</span>
              </div>
            </button>
          ))}
        </div>}
      </div>

      {selectedItem && (
        <div className="fixed inset-0 z-50 bg-navy-dark/90 flex items-center justify-center p-4 animate-fade-in" onClick={() => setSelected(null)}>
          <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-primary-foreground/80 hover:text-primary-foreground z-10">
            <X className="w-8 h-8" />
          </button>
          {selectedItem.type === "image" ? (
            <img src={selectedItem.src} alt={selectedItem.alt} width={1200} height={1200} decoding="async" className="max-w-full max-h-[85vh] object-contain rounded-xl" />
          ) : (
            <video
              ref={videoRef}
              src={selectedItem.src}
              controls
              autoPlay
              muted
              loop
              className={`max-w-full max-h-[85vh] rounded-xl ${selectedItem.mirror ? '-scale-x-100' : ''}`}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default GallerySection;
